import * as React from 'react';
import { View, Text, Image } from '@react-pdf/renderer';
import PDFDocument, { PDFConfig } from '../base/PDFDocument';
import PDFPatientInfo from '../base/PDFPatientInfo';
import { PDFMetadataFooterData } from '../base/PDFMetadataFooter';
import { commonPDFStyles } from '../styles/pdfStyles';
import { typographyStyles } from '../styles/typography';
import { createTwoColumnLayout } from '../styles/layout';
import { PDFSurveyRecordRenderer } from '../utils/contentRenderer';
import { formatDateForPDF, formatPatientName } from '../utils/dataFormatters';
import { createT } from '../utils/simpleTranslate';
import { PDFFieldRenderer } from '../utils/PDFFieldRenderer';
import { resolveRoleTitle } from '../utils/researcherRole';
import { groupRecordsForPDF } from '../utils/groupRecordsForPDF';
import { shouldShowSurveyFieldLabel } from '../../../utils/shouldShowSurveyFieldLabel';
import { extractSurveyNameSnapshot } from '../../general/SmartFields/Templated/extractSurveyNameSnapshot';

// Helper function for researcher names
const formatResearcherName = (researcher: IResearcher): string => {
  return `${researcher.name} ${researcher.surnames}`.trim();
};

/**
 * Submission PDF Document Component
 * 
 * Handles patient survey submissions and form responses.
 * Displays survey sections with their corresponding field responses.
 */

interface IPatient {
  personalData: {
    name?: string;
    surnames?: string;
    birthdate?: string | Date;
    sex?: string;
    phone?: string;
    healthId?: string;
    nationalId?: string;
    passportNumber?: string;
    insurance?: string | number;
  };
}

interface IResearcher {
  uuid: string;
  name: string;
  surnames: string;
}

interface ISurveyField {
  id: string | number;
  uuid?: string;
  name: string;
  label: string;
  type: string;
  order: number;
  options?: string[] | any[];
}

interface ISurveySection {
  id?: number;
  uuid: string;
  name: string;
  order: number;
  fields: ISurveyField[];
}

interface ISurvey {
  uuid: string;
  name: string;
  sections: { [key: string]: ISurveySection } | ISurveySection[];
}

interface ISurveyRecord {
  id: string | number;
  value: any;
  surveyField: ISurveyField;
  surveySection?: any;
}

interface ISubmission {
  id: string | number;
  createdAt: string | Date;
  updatedAt: string | Date;
  uuidResearcher?: string;
  researcher?: IResearcher | string;
  records?: ISurveyRecord[];
  surveyRecords?: ISurveyRecord[];
}

interface SubmissionPDFDocumentProps {
  submission: ISubmission;
  currentSurvey: ISurvey;
  patient: IPatient;
  prescribingDoctor?: IResearcher;
  /** SHER-224: role name of the prescribing/uploading researcher (e.g. "DOCTOR", "NURSE"). */
  prescribingDoctorRole?: string;
  config: PDFConfig;
  nameSurvey: string;
  locale?: string;
  uuidInvestigation: string; // Investigation UUID (for reference/tracking)
  signatureBase64?: string | null; // Base64-encoded signature image from parent component (if available)
}

export const SubmissionPDFDocument: React.FC<SubmissionPDFDocumentProps> = ({
  submission,
  currentSurvey,
  patient,
  prescribingDoctor,
  prescribingDoctorRole,
  config,
  nameSurvey,
  locale = 'fr',
  uuidInvestigation,
  signatureBase64,
}) => {
  const t = createT(locale);

  // SHER-246 — Group records by their LIVE section if the field still
  // exists, or into an `orphans` bucket if the field has been deactivated
  // since the submission was created. The old logic iterated
  // currentSurvey.sections and tried to match each record to a live section
  // by field id; any record whose field no longer existed was silently
  // dropped, leaving the historical PDF blank for any submission that
  // predated a template rename / field-delete. groupRecordsForPDF inverts
  // that: every record reaches a bucket — orphans included — so the
  // medico-legal artifact reproduces the saved content.
  const grouped = React.useMemo(() => {
    const records = submission.records || submission.surveyRecords || [];
    const sections = Array.isArray(currentSurvey.sections)
      ? currentSurvey.sections
      : (Object.values(currentSurvey.sections) as ISurveySection[]);
    return groupRecordsForPDF(records, sections);
  }, [submission, currentSurvey.sections]);

  // SHER-244 — the PDF title (and the first-section auto-hide logic
  // below) must show the survey name AT THE TIME OF SUBMISSION, not the
  // current live survey.name. Renaming the live template would otherwise
  // retroactively rewrite the title of every historical PDF (Pedro,
  // comment 12914 on SHER-208). The snapshot lives inside the
  // medical_history_template record's JSON value under the
  // `template_medical_history-survey_name` key, written by
  // TemplatedMedicalHistoryEditor at save time. Avoids a DB migration:
  // older records without the key fall back to the live nameSurvey.
  const effectiveSurveyName = React.useMemo(() => {
    const records = submission.records || submission.surveyRecords || [];
    return extractSurveyNameSnapshot(records as any) ?? nameSurvey;
  }, [submission, nameSurvey]);

  const renderSurveySection = (
    section: ISurveySection,
    sectionRecords: ISurveyRecord[],
    index: number,
  ) => {
    if (!sectionRecords.length) {
      // Nothing recorded for this section — skip it entirely rather than
      // emit an empty header with placeholder dashes (SHER-246 issue A).
      return null;
    }
    const isFirstSection = index === 0;
    const shouldHideTitle = isFirstSection && section.name === effectiveSurveyName;

    return (
      <View key={section.uuid} style={[commonPDFStyles.section]}>
        {!shouldHideTitle && (
          <Text style={typographyStyles.h4}>
            {section.name}
          </Text>
        )}
        <View style={commonPDFStyles.sectionContent}>
          {sectionRecords.map((record) =>
            renderSurveyField(record.surveyField, record, section),
          )}
        </View>
      </View>
    );
  };

  // SHER-246 — orphan records (whose field is no longer in the live
  // template) render at the bottom of the document without a special
  // "Archived fields" header, so historical PDFs read as natural medical
  // notes (SHER-208 sub-issue C). The original surveyField name embedded
  // in each record carries the label, so the content keeps its original
  // form even if the live template was later renamed.
  const renderOrphans = () => {
    if (!grouped.orphans.length) return null;
    return (
      <View style={[commonPDFStyles.section]}>
        <View style={commonPDFStyles.sectionContent}>
          {grouped.orphans.map((record) =>
            renderSurveyField(record.surveyField, record),
          )}
        </View>
      </View>
    );
  };

  const renderSurveyField = (
    field: ISurveyField,
    record?: ISurveyRecord,
    section?: ISurveySection,
  ) => {
    const showFieldName = section
      ? shouldShowSurveyFieldLabel(section, field)
      : true;

    return (
      <View key={field.id} style={[commonPDFStyles.marginBottom]}>
        {showFieldName && (
          <Text style={typographyStyles.subtitle2}>
            {field.name}:
          </Text>
        )}
        
        <View style={{ paddingLeft: 8 }}>
          {renderFieldValue(field, record)}
        </View>
      </View>
    );
  };

  const renderFieldValue = (field: ISurveyField, record?: ISurveyRecord) => {
    // Use unified field renderer
    return (
      <PDFFieldRenderer
        field={field}
        value={record?.value}
        locale={locale}
      />
    );
  };

  const renderSubmissionInfo = () => {
    return (
      <View style={[commonPDFStyles.section]}>
        <Text style={typographyStyles.h4}>
          {t('hospital.submission.info')}
        </Text>
        
        <View style={commonPDFStyles.sectionContent}>
          <Text style={typographyStyles.body2}>
            <Text style={typographyStyles.button}>{t('pages.request.pdf.created_at')}</Text>: {formatDateForPDF(submission.createdAt, locale)}
          </Text>
          
          {submission.updatedAt !== submission.createdAt && (
            <Text style={typographyStyles.body2}>
              <Text style={typographyStyles.button}>{t('pages.request.pdf.updated_at')}</Text>: {formatDateForPDF(submission.updatedAt, locale)}
            </Text>
          )}
        </View>
      </View>
    );
  };

  // SHER-224: the signature title reflects the uploader's actual role. It is
  // omitted entirely when the role is unknown, so a non-clinician is never
  // labelled "Médecin Prescripteur".
  const signatureRoleTitle = resolveRoleTitle(prescribingDoctorRole, t);

  const renderFooter = () => {
    if (!prescribingDoctor) return null;

    return (
      <View style={[commonPDFStyles.signature, commonPDFStyles.pageBreakAvoid]}>
        {/* Signature placeholder (label + name + role) */}
        <Text style={commonPDFStyles.signatureName}>
          {t('general.signature')}
        </Text>
        <Text style={commonPDFStyles.signatureName}>
          {formatResearcherName(prescribingDoctor)}
        </Text>
        {signatureRoleTitle && (
          <Text style={commonPDFStyles.signatureTitle}>
            {signatureRoleTitle}
          </Text>
        )}

        {/* Signature image below placeholder */}
        {signatureBase64 && (
          <View style={{ marginTop: 10, alignItems: 'center' }}>
            <Image 
              src={signatureBase64} 
              style={{ 
                width: 150, 
                height: 75, 
                objectFit: 'contain',
              }} 
            />
          </View>
        )}
      </View>
    );
  };

  // Sort the bucketed sections by their original order so the PDF mirrors
  // the layout of the live template, then append orphan records at the
  // bottom.
  const sortedGroups = [...grouped.sections].sort(
    (a, b) => (a.section.order ?? 0) - (b.section.order ?? 0),
  );

  return (
    <PDFDocument
      title={effectiveSurveyName}
      patientInfo={<PDFPatientInfo patient={patient} locale={locale} />}
      config={config}
      locale={locale}
      footer={renderFooter()}
      metadataFooter={{
        patient: patient as PDFMetadataFooterData['patient'],
        documentReference: String(submission.id),
        documentDate: submission.createdAt,
        validatedBy: prescribingDoctor
          ? {
              name: formatResearcherName(prescribingDoctor),
              title: signatureRoleTitle || '',
            }
          : undefined,
        locale,
        hospitalName: config.hospitalName,
        city: config.city,
      }}
    >
      {renderSubmissionInfo()}

      {sortedGroups.map(({ section, records }, index) =>
        renderSurveySection(section, records, index),
      )}
      {renderOrphans()}
    </PDFDocument>
  );
};

export default SubmissionPDFDocument;
